import axios from "axios";

// ═══════════════════════════════════════════════════════════════
//  Inventory API — wraps real backend endpoints:
//    • MRP             /v1/mrp/*
//    • GRN             /v1/grn/*
//    • Wastage         /v1/wastage/*
//    • Purchase        /v1/purchase-request/* , /v1/purchase-order/*
//    • Reports         /v1/reports/*
//    • Raw Materials   /v1/inventory/raw-material/*
//    • Stock           /v1/inventory/stock/*
//    • Movements       /v1/inventory/movements/*
// ═══════════════════════════════════════════════════════════════

// ── Raw Materials CRUD (/v1/inventory/raw-material) ──────────

/**
 * Get all raw materials (paginated).
 * GET /v1/inventory/raw-material
 * @param {{ page, limit, category, search, active_only }} params
 */
export const getAllRawMaterials = async (params = {}) => {
  const response = await axios.get("inventory/raw-material", { params });
  return response.data;
};

/**
 * Get a single raw material by ID (includes stock summary).
 * GET /v1/inventory/raw-material/:id
 */
export const getRawMaterialById = async (id) => {
  const response = await axios.get(`inventory/raw-material/${id}`);
  return response.data;
};

/**
 * Get all active materials for a given category.
 * GET /v1/inventory/raw-material/category/:category
 * @param {string} category  e.g. PAPER, INK, GUM_CORR
 */
export const getRawMaterialsByCategory = async (category) => {
  const response = await axios.get(`inventory/raw-material/category/${category}`);
  return response.data;
};

/**
 * Get materials where current stock ≤ reorder_level.
 * GET /v1/inventory/raw-material/low-stock
 */
export const getLowStockMaterials = async () => {
  const response = await axios.get("inventory/raw-material/low-stock");
  return response.data;
};

/**
 * Create a new raw material.
 * POST /v1/inventory/raw-material
 */
export const createRawMaterial = async (data) => {
  const response = await axios.post("inventory/raw-material", data);
  return response.data;
};

/**
 * Update a raw material (code cannot be changed).
 * PUT /v1/inventory/raw-material/:id
 */
export const updateRawMaterial = async (id, data) => {
  const response = await axios.put(`inventory/raw-material/${id}`, data);
  return response.data;
};

/**
 * Soft-delete (deactivate) a raw material.
 * DELETE /v1/inventory/raw-material/:id
 */
export const deleteRawMaterial = async (id) => {
  const response = await axios.delete(`inventory/raw-material/${id}`);
  return response.data;
};

// ── Stock (/v1/inventory/stock) ──────────────────────────────

/**
 * Get all stock grouped by material.
 * GET /v1/inventory/stock
 * @param {{ category, location_id, quality_status }} params
 */
export const getAllStock = async (params = {}) => {
  const response = await axios.get("inventory/stock", { params });
  return response.data;
};

/**
 * Get stock entries for a specific material.
 * GET /v1/inventory/stock/material/:materialId
 */
export const getStockByMaterial = async (materialId) => {
  const response = await axios.get(`inventory/stock/material/${materialId}`);
  return response.data;
};

/**
 * Receive stock (from GRN or manual entry).
 * POST /v1/inventory/stock/receive
 */
export const receiveStock = async (data) => {
  const response = await axios.post("inventory/stock/receive", data);
  return response.data;
};

/**
 * Issue stock to a production job.
 * POST /v1/inventory/stock/issue
 */
export const issueStock = async (data) => {
  const response = await axios.post("inventory/stock/issue", data);
  return response.data;
};

/**
 * Adjust stock quantity (physical count correction).
 * POST /v1/inventory/stock/adjust
 */
export const adjustStock = async (data) => {
  const response = await axios.post("inventory/stock/adjust", data);
  return response.data;
};

/**
 * Transfer stock between locations.
 * POST /v1/inventory/stock/transfer
 */
export const transferStock = async (data) => {
  const response = await axios.post("inventory/stock/transfer", data);
  return response.data;
};

/**
 * Reserve stock for a planned job.
 * POST /v1/inventory/stock/reserve
 */
export const reserveStock = async (data) => {
  const response = await axios.post("inventory/stock/reserve", data);
  return response.data;
};

/**
 * Release reserved stock.
 * POST /v1/inventory/stock/release
 */
export const releaseStock = async (data) => {
  const response = await axios.post("inventory/stock/release", data);
  return response.data;
};

// ── Movements (/v1/inventory/movements) ──────────────────────

/**
 * Get all stock movements (audit trail).
 * GET /v1/inventory/movements
 * @param {{ movement_type, from_date, to_date, page, limit }} params
 */
export const getAllMovements = async (params = {}) => {
  const response = await axios.get("inventory/movements", { params });
  return response.data;
};

export const getInventoryHistory = async (params = {}) => {
  const response = await axios.get("inventory/movements", { params });
  return response.data;
};

/**
 * Get movements for a specific material.
 * GET /v1/inventory/movements/material/:materialId
 */
export const getMovementsByMaterial = async (materialId, params = {}) => {
  const response = await axios.get(`inventory/movements/material/${materialId}`, { params });
  return response.data;
};

/**
 * Get movements for a specific job.
 * GET /v1/inventory/movements/job/:jobId
 */
export const getMovementsByJob = async (jobId) => {
  const response = await axios.get(`inventory/movements/job/${jobId}`);
  return response.data;
};

export const markInventoryConsumed = async (data) => {
  const response = await axios.post("inventory/stock/consume", data);
  return response.data;
};

export const getInventorySettlements = async (params = {}) => {
  const response = await axios.get("inventory/settlements", { params });
  return response.data;
};

export const createInventorySettlement = async (data) => {
  const response = await axios.post("inventory/settlements", data);
  return response.data;
};

// ── MRP (Material Requirement Planning) ──────────────────────

/**
 * Calculate / preview BOM for a job.
 * POST /v1/mrp/bom/:jobId
 */
export const calculateBOM = async (jobId) => {
  const response = await axios.post(`mrp/bom/${jobId}`);
  return response.data;
};

/**
 * Run MRP for a single job (saves requirements).
 * POST /v1/mrp/run/:jobId
 */
export const runMRP = async (jobId) => {
  const response = await axios.post(`mrp/run/${jobId}`);
  return response.data;
};

/**
 * Run MRP for multiple jobs.
 * POST /v1/mrp/run-batch
 * @param {string[]} jobIds
 */
export const runMRPBatch = async (jobIds) => {
  const response = await axios.post("mrp/run-batch", { job_ids: jobIds });
  return response.data;
};

/**
 * Get material requirements for a specific job.
 * GET /v1/mrp/job/:jobId/requirements
 */
export const getJobRequirements = async (jobId) => {
  const response = await axios.get(`mrp/job/${jobId}/requirements`);
  return response.data;
};

/**
 * Get all material shortages (aggregated).
 * GET /v1/mrp/shortages
 * @param {{ stage?, material_id? }} params
 */
export const getShortages = async (params = {}) => {
  const response = await axios.get("mrp/shortages", { params });
  return response.data;
};

/**
 * Generate a Purchase Request from a shortage.
 * POST /v1/mrp/shortages/generate-pr
 * @param {{ material_id, quantity, unit, job_ids[], notes? }} data
 */
export const generatePRFromShortage = async (data) => {
  const response = await axios.post("mrp/shortages/generate-pr", data);
  return response.data;
};

// ── GRN (Goods Receipt Notes) ────────────────────────────────

/**
 * Get all GRNs (paginated).
 * GET /v1/grn
 * @param {{ page, count, status, qc_status, supplier_id, purchase_order_id, search, start_date, end_date }} params
 */
export const getAllGRNs = async (params = {}) => {
  const response = await axios.get("grn", { params });
  return response.data;
};

/**
 * Get a GRN by ID.
 * GET /v1/grn/:id
 */
export const getGRNById = async (id) => {
  const response = await axios.get(`grn/${id}`);
  return response.data;
};

/**
 * Create a new GRN.
 * POST /v1/grn
 */
export const createGRN = async (data) => {
  const response = await axios.post("grn", data);
  return response.data;
};

/**
 * Update a draft GRN.
 * PUT /v1/grn/:id
 */
export const updateGRN = async (id, data) => {
  const response = await axios.put(`grn/${id}`, data);
  return response.data;
};

/**
 * Submit a GRN for approval.
 * POST /v1/grn/:id/submit
 */
export const submitGRN = async (id) => {
  const response = await axios.post(`grn/${id}/submit`);
  return response.data;
};

/**
 * Approve a submitted GRN.
 * POST /v1/grn/:id/approve
 * Side effects: updates PO received_quantity, pending_quantity, PO status.
 */
export const approveGRN = async (id) => {
  const response = await axios.post(`grn/${id}/approve`);
  return response.data;
};

/**
 * Reject a submitted GRN.
 * POST /v1/grn/:id/reject
 * @param {string} reason
 */
export const rejectGRN = async (id, reason) => {
  const response = await axios.post(`grn/${id}/reject`, { reason });
  return response.data;
};

/**
 * Update QC status on a GRN.
 * PATCH /v1/grn/:id/qc
 * @param {{ qc_status, qc_notes, item_results[] }} data
 */
export const updateGRNQC = async (id, data) => {
  const response = await axios.patch(`grn/${id}/qc`, data);
  return response.data;
};

// ── Wastage / Excess Stock (Semi-Processed Inventory) ────────

/**
 * Get excess stock items (semi-processed / WIP).
 * GET /v1/wastage/excess
 * @param {{ page, count, status, ply, customer_id }} params
 */
export const getExcessStock = async (params = {}) => {
  const response = await axios.get("wastage/excess", { params });
  return response.data;
};

/**
 * Create excess stock entry.
 * POST /v1/wastage/excess
 */
export const createExcessStock = async (data) => {
  const response = await axios.post("wastage/excess", data);
  return response.data;
};

/**
 * Find matching excess stock for reuse.
 * POST /v1/wastage/excess/find-match
 * @param {{ roll_size, cutting_size, ply, flute_type, customer_id?, quantity_needed }} data
 */
export const findMatchingExcess = async (data) => {
  const response = await axios.post("wastage/excess/find-match", data);
  return response.data;
};

/**
 * Use excess stock for a job.
 * POST /v1/wastage/excess/:id/use
 * @param {{ job_id, quantity }} data
 */
export const applyExcessStock = async (id, data) => {
  const response = await axios.post(`wastage/excess/${id}/use`, data);
  return response.data;
};

/**
 * Get wastage analytics.
 * GET /v1/wastage/analytics
 * @param {{ start_date, end_date, stage_id?, machine_id? }} params
 */
export const getWastageAnalytics = async (params = {}) => {
  const response = await axios.get("wastage/analytics", { params });
  return response.data;
};

/**
 * Get wastage trend.
 * GET /v1/wastage/trend
 * @param {{ days }} params  (default 30)
 */
export const getWastageTrend = async (params = {}) => {
  const response = await axios.get("wastage/trend", { params });
  return response.data;
};

/**
 * Record a wastage entry.
 * POST /v1/wastage
 */
export const recordWastage = async (data) => {
  const response = await axios.post("wastage", data);
  return response.data;
};

/**
 * Get wastage reasons list.
 * GET /v1/wastage/reasons
 * @param {{ category?, stage? }} params
 */
export const getWastageReasons = async (params = {}) => {
  const response = await axios.get("wastage/reasons", { params });
  return response.data;
};

/**
 * Get buffer configuration.
 * GET /v1/wastage/buffer/config
 */
export const getBufferConfig = async () => {
  const response = await axios.get("wastage/buffer/config");
  return response.data;
};

// ── Purchase Requests ────────────────────────────────────────

/**
 * Get all purchase requests.
 * GET /v1/purchase-request
 */
export const getAllPurchaseRequests = async (params = {}) => {
  const response = await axios.get("purchase-request", { params });
  return response.data;
};

/**
 * Get purchase request by ID.
 * GET /v1/purchase-request/:id
 */
export const getPurchaseRequestById = async (id) => {
  const response = await axios.get(`purchase-request/${id}`);
  return response.data;
};

/**
 * Create a purchase request.
 * POST /v1/purchase-request
 */
export const createPurchaseRequest = async (data) => {
  const response = await axios.post("purchase-request", data);
  return response.data;
};

/**
 * Submit a purchase request for approval.
 * POST /v1/purchase-request/:id/submit
 */
export const submitPurchaseRequest = async (id) => {
  const response = await axios.post(`purchase-request/${id}/submit`);
  return response.data;
};

/**
 * Approve a purchase request.
 * POST /v1/purchase-request/:id/approve
 */
export const approvePurchaseRequest = async (id) => {
  const response = await axios.post(`purchase-request/${id}/approve`);
  return response.data;
};

/**
 * Convert purchase request to purchase order.
 * POST /v1/purchase-request/:id/convert
 */
export const convertPRtoPO = async (id, data) => {
  const response = await axios.post(`purchase-request/${id}/convert`, data);
  return response.data;
};

// ── Purchase Orders ──────────────────────────────────────────

/**
 * Get all purchase orders.
 * GET /v1/purchase-order
 */
export const getAllPurchaseOrders = async (params = {}) => {
  const response = await axios.get("purchase-order", { params });
  return response.data;
};

/**
 * Get purchase order by ID.
 * GET /v1/purchase-order/:id
 */
export const getPurchaseOrderById = async (id) => {
  const response = await axios.get(`purchase-order/${id}`);
  return response.data;
};

// ── Reports ──────────────────────────────────────────────────

/**
 * Get material consumption report.
 * GET /v1/reports/material-consumption
 * @param {{ from_date, to_date, category? }} params
 */
export const getMaterialConsumption = async (params = {}) => {
  const response = await axios.get("reports/material-consumption", { params });
  return response.data;
};

/**
 * Get production report.
 * GET /v1/reports/production
 * @param {{ from_date, to_date }} params
 */
export const getProductionReport = async (params = {}) => {
  const response = await axios.get("reports/production", { params });
  return response.data;
};

/**
 * Get wastage report.
 * GET /v1/reports/wastage
 * @param {{ from_date, to_date }} params
 */
export const getWastageReport = async (params = {}) => {
  const response = await axios.get("reports/wastage", { params });
  return response.data;
};

// ── Non-Consumable Assets CRUD (/v1/inventory/non-consumable) ─

export const getAllNonConsumables = async (params = {}) => {
  const response = await axios.get("inventory/non-consumable", { params });
  return response.data;
};

export const getNonConsumableById = async (id) => {
  const response = await axios.get(`inventory/non-consumable/${id}`);
  return response.data;
};

export const createNonConsumable = async (data) => {
  const response = await axios.post("inventory/non-consumable", data);
  return response.data;
};

export const updateNonConsumable = async (id, data) => {
  const response = await axios.put(`inventory/non-consumable/${id}`, data);
  return response.data;
};

export const deleteNonConsumable = async (id) => {
  const response = await axios.delete(`inventory/non-consumable/${id}`);
  return response.data;
};

export const getNonConsumablesByRunningSize = async (runningSizeId) => {
  const response = await axios.get(`inventory/non-consumable/by-running-size/${runningSizeId}`);
  return response.data;
};

export default {
  // Raw Materials
  getAllRawMaterials,
  getRawMaterialById,
  getRawMaterialsByCategory,
  getLowStockMaterials,
  createRawMaterial,
  updateRawMaterial,
  deleteRawMaterial,
  // Stock
  getAllStock,
  getStockByMaterial,
  receiveStock,
  issueStock,
  adjustStock,
  transferStock,
  reserveStock,
  releaseStock,
  // Movements
  getAllMovements,
  getMovementsByMaterial,
  getMovementsByJob,
  // MRP
  calculateBOM,
  runMRP,
  runMRPBatch,
  getJobRequirements,
  getShortages,
  generatePRFromShortage,
  // GRN
  getAllGRNs,
  getGRNById,
  createGRN,
  updateGRN,
  submitGRN,
  approveGRN,
  rejectGRN,
  updateGRNQC,
  // Excess / WIP
  getExcessStock,
  createExcessStock,
  findMatchingExcess,
  applyExcessStock,
  getWastageAnalytics,
  getWastageTrend,
  recordWastage,
  getWastageReasons,
  getBufferConfig,
  // Purchase Requests
  getAllPurchaseRequests,
  getPurchaseRequestById,
  createPurchaseRequest,
  submitPurchaseRequest,
  approvePurchaseRequest,
  convertPRtoPO,
  // Purchase Orders
  getAllPurchaseOrders,
  getPurchaseOrderById,
  // Reports
  getMaterialConsumption,
  getProductionReport,
  getWastageReport,
};
